<script lang="ts" setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import api from "@/services/api";

const route = useRoute();
const router = useRouter();

const loading = ref(true);
const error = ref("");
const success = ref(false);
const message = ref("");
const partnerOrgName = ref("");
const clientOrgName = ref("");

onMounted(async () => {
  const token = route.query.token as string;

  if (!token) {
    error.value = "Invalid invitation link - no token provided";
    loading.value = false;
    return;
  }

  try {
    const response = await api.post(`/partnerships/accept`, {
      invitationToken: token
    });

    if (response.ok) {
      const data = await response.json();
      success.value = true;
      message.value = "Partnership successfully accepted!";

      // Note: The API returns partnerOrgId and clientOrgId,
      // but not the org names. For better UX, we could enhance the API
      // to include org names in the response

      // Redirect to home after a short delay
      setTimeout(() => {
        router.push({ name: "home" });
      }, 3000);
    } else {
      const errorText = await response.text();
      error.value = errorText || "Failed to accept partnership invitation";
    }
  } catch (err: any) {
    error.value = err.message || "An error occurred while accepting the invitation";
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="min-vh-100 d-flex align-items-center justify-content-center">
    <div class="card partnership-card shadow">
      <div class="card-body text-center">
        <h1 class="visually-hidden">Accept Partnership</h1>
        <img src="/images/Blackbox-Logo.png" alt="BlackBox" class="logo mb-4" />

        <div v-if="loading" class="py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Processing partnership invitation...</span>
          </div>
          <p class="mt-3 text-muted">Processing your partnership invitation...</p>
        </div>

        <div v-else-if="success" class="py-4">
          <i class="bi bi-check-circle-fill text-success display-1"></i>
          <h2 class="mt-3">Partnership Accepted!</h2>
          <p class="text-muted">{{ message }}</p>
          <div class="mt-3">
            <div class="alert alert-success" role="alert">
              <i class="bi bi-handshake me-2"></i>
              Your organizations are now partners. You can now access shared products and workflows.
            </div>
          </div>
          <p class="small text-muted">Redirecting to dashboard...</p>
        </div>

        <div v-else-if="error" class="py-4">
          <i class="bi bi-x-circle-fill text-danger display-1"></i>
          <h2 class="mt-3">Invitation Error</h2>
          <p class="text-danger">{{ error }}</p>
          <div class="mt-3">
            <div class="alert alert-warning" role="alert">
              <small>
                <strong>Common reasons for errors:</strong>
                <ul class="mb-0 mt-1 text-start">
                  <li>The invitation link has expired</li>
                  <li>The invitation has already been accepted</li>
                  <li>You don't have permission to accept (requires Owner or OrgAdmin role)</li>
                  <li>You're not logged in to the correct organization</li>
                </ul>
              </small>
            </div>
          </div>
          <div class="mt-4">
            <router-link to="/" class="bb-btn-primary-cta">
              <i class="bi bi-house me-2"></i>
              Go to Dashboard
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.partnership-card {
  max-width: 550px;
  width: 100%;
  margin: 1rem;
}

.logo {
  max-width: 200px;
  height: auto;
}
</style>
